/**
 * Behaviour for the shared kanban board (App\Controls\KanbanBoardControl).
 *
 *  - A card may only move between the columns of its own board; order inside a column is
 *    not persisted.
 *  - The origin column is remembered on drag start, and every refusal (server "rejected",
 *    network error, cancelled prompt) restores the card there. No column name is hardcoded.
 *  - Moves go out with nette.ajax's `unique` extension OFF, so a quick second drag cannot
 *    abort the first request after the server already saved it.
 *  - A move may also answer `pending` (payload `{status:'pending', modal}`): the card stays
 *    where it was dropped, `modal` opens in the shared `.modal-ajax`, and the move is either
 *    committed — payload `kanbanCommit` (picked up by the guarded `kanban` nette.ajax
 *    extension below) or re-sent — payload `kanbanResend` / `window.finestKanban.resend(cardId)`
 *    sending the original move again — or reverted when the modal closes without either
 *    (`hidden.bs.modal`).
 *  - An `ok` move may arrive together with the redrawn board snippet (the source asked for it,
 *    `KanbanMoveResult::withRedraw()`): the new board is wired by `init()` on `ajaxComplete`, the
 *    old one is left alone.
 *  - A move may answer `confirm` (payload `{status:'confirm', message}`): the user is asked
 *    first; yes re-sends the same move with `input.confirmed = "1"` (kept by a later `pending`
 *    round-trip), no reverts the card.
 *  - Columns may show a running total of `data-kanban-value` per card in `.js-kanban-total`,
 *    formatted with `data-kanban-locale`; it is computed client-side and kept in sync by
 *    `refresh()`, never sent by the server beyond the initial "0".
 *  - Any element inside a card marked `data-kanban-move-to="<columnId>"` (e.g. a ⋯ menu item)
 *    triggers the same move as dragging the card onto that column, including its prompt. The
 *    target column does not have to be in the DOM (a source may not always render every
 *    column) — when it is, the card is optimistically moved into it like a drag, revert()
 *    included; when it is not, the card is only removed on a successful move and the prompt
 *    text comes from the trigger's own `data-kanban-prompt` instead of the column's.
 *  - `[data-kanban-drop="<columnId>"]` outside the board (`data-kanban-board-for` selects which
 *    board(s)) is a sortable drop target shown only while dragging; dropping on it runs the same
 *    move as `data-kanban-move-to` with its own `data-kanban-prompt`.
 */
(function (window, $) {
    "use strict";

    if (typeof $ !== "function" || !$.fn || !$.fn.sortable || !$.nette) {
        return;
    }

    var SEL = {
        root: ".js-kanban",
        list: ".js-kanban-cards",
        card: ".js-kanban-card",
        count: ".js-kanban-count",
        total: ".js-kanban-total",
        column: "[data-kanban-column]",
        moveTrigger: "[data-kanban-move-to]",
        dropZone: "[data-kanban-drop]",
    };

    var READY = "kanbanReady";
    var ORIGIN = "kanbanOrigin";

    // The move that is waiting on a modal round-trip (KanbanMoveResult::pending()). Only one
    // move can be in flight at a time: the board disables dragging while a request is out, and
    // the modal is modal, so a second pending move cannot start before this one resolves.
    var PENDING = null;

    function texts($root) {
        try {
            return JSON.parse($root.attr("data-kanban-i18n") || "{}");
        } catch (e) {
            return {};
        }
    }

    function locale($root) {
        return $root.attr("data-kanban-locale") || "cs-CZ";
    }

    function updateCounts($root) {
        $root.find(SEL.column).each(function () {
            var $column = $(this);
            $column.find(SEL.count).first().text($column.find(SEL.card).length);
        });
    }

    function updateTotals($root) {
        var loc = locale($root);

        $root.find(SEL.column).each(function () {
            var $column = $(this);
            var $total = $column.find(SEL.total).first();

            if (!$total.length) {
                return;
            }

            var sum = 0;
            $column.find(SEL.card).each(function () {
                sum += parseFloat($(this).attr("data-kanban-value")) || 0;
            });
            $total.text(sum.toLocaleString(loc, { maximumFractionDigits: 0 }));
        });
    }

    function refresh($root) {
        updateCounts($root);
        updateTotals($root);
    }

    function revert($root, $card) {
        var origin = $card.data(ORIGIN);
        if (origin) {
            var $sibling = origin.list.children(SEL.card).eq(origin.index);
            if ($sibling.length) {
                $card.insertBefore($sibling);
            } else {
                origin.list.append($card);
            }
        }
        refresh($root);
    }

    function alertError($root, message) {
        var t = texts($root);
        if (window.Swal) {
            window.Swal.fire({ icon: "error", title: t.errorTitle || "", text: message || t.error || "" });
        } else {
            window.alert(message || t.error || "");
        }
    }

    function askFollowUp(followUp, t) {
        if (!followUp || !followUp.url) {
            return;
        }
        if (!window.Swal) {
            if (window.confirm(followUp.message)) {
                window.location.href = followUp.url;
            }
            return;
        }
        window.Swal.fire({
            icon: "question",
            text: followUp.message,
            showCancelButton: true,
            confirmButtonText: followUp.confirm || t.confirm,
            cancelButtonText: t.cancel,
        }).then(function (result) {
            if (result.isConfirmed) {
                window.location.href = followUp.url;
            }
        });
    }

    function askConfirm($root, $card, columnId, input, onSuccess, message) {
        var t = texts($root);

        function proceed() {
            send($root, $card, columnId, $.extend({}, input, { confirmed: "1" }), onSuccess);
        }

        if (!window.Swal) {
            if (window.confirm(message)) {
                proceed();
            } else {
                revert($root, $card);
            }
            return;
        }
        window.Swal.fire({
            icon: "question",
            text: message,
            showCancelButton: true,
            confirmButtonText: t.confirm,
            cancelButtonText: t.cancel,
        }).then(function (result) {
            if (result.isConfirmed) {
                proceed();
            } else {
                revert($root, $card);
            }
        });
    }

    function send($root, $card, columnId, input, onSuccess) {
        var t = texts($root);
        var $lists = $root.find(SEL.list);
        // Runs after the busy state is released, so a re-send starts from a clean slate.
        var afterRelease = null;

        // No second drag while this move is pending: it would overwrite the remembered origin,
        // so a rejected first move could revert the card into the wrong column.
        $lists.sortable("option", "disabled", true);
        $root.addClass("is-busy");
        $.nette
            .ajax({
                type: "POST",
                url: $root.attr("data-kanban-move"),
                data: { card: $card.attr("data-kanban-card"), column: columnId, input: input },
                off: ["unique"],
            })
            .done(function (payload) {
                if (payload && payload.status === "pending" && payload.modal) {
                    PENDING = { root: $root, card: $card, cardId: $card.attr("data-kanban-card"), columnId: columnId, input: input, onSuccess: onSuccess };
                    refresh($root);
                    $.nette.ajax({ url: payload.modal });
                    return;
                }
                if (payload && payload.status === "confirm") {
                    afterRelease = function () {
                        askConfirm($root, $card, columnId, input, onSuccess, payload.message || "");
                    };
                    return;
                }
                if (!payload || payload.status !== "ok") {
                    revert($root, $card);
                    alertError($root, payload && payload.message);
                    return;
                }
                if (onSuccess) {
                    onSuccess();
                } else {
                    refresh($root);
                }
                askFollowUp(payload.followUp, t);
            })
            .fail(function () {
                revert($root, $card);
                alertError($root, null);
            })
            .always(function () {
                // A move may answer with the board redrawn (KanbanMoveResult::withRedraw()). The
                // snippet is swapped before this runs, and the old lists lost their sortable with it.
                if (!$.contains(document.documentElement, $root[0])) {
                    return;
                }
                $root.removeClass("is-busy");
                $lists.sortable("option", "disabled", false);
                if (afterRelease) {
                    afterRelease();
                }
            });
    }

    function promptThenSend($root, $card, columnId, prompt, onSuccess) {
        var t = texts($root);

        if (!window.Swal) {
            var value = window.prompt(prompt, "");
            if (value === null || $.trim(value) === "") {
                revert($root, $card);
                return;
            }
            send($root, $card, columnId, { reason: value }, onSuccess);
            return;
        }

        window.Swal.fire({
            title: prompt,
            input: "textarea",
            inputAttributes: { maxlength: 500 },
            showCancelButton: true,
            confirmButtonText: t.confirm,
            cancelButtonText: t.cancel,
            inputValidator: function (value) {
                return $.trim(value || "") === "" ? t.required : undefined;
            },
        }).then(function (result) {
            if (!result.isConfirmed) {
                revert($root, $card);
                return;
            }
            send($root, $card, columnId, { reason: result.value }, onSuccess);
        });
    }

    function findColumn($root, columnId) {
        return $root.find(SEL.column).filter(function () {
            return $(this).attr("data-kanban-column") === columnId;
        });
    }

    function moveCardTo($root, $card, columnId, fallbackPrompt) {
        var $column = findColumn($root, columnId);
        var onSuccess = null;

        $card.data(ORIGIN, { list: $card.parent(), index: $card.index() });

        if ($column.length) {
            $column.find(SEL.list).first().append($card);
        } else {
            onSuccess = function () {
                $card.remove();
                refresh($root);
            };
        }

        var prompt = ($column.length ? $column.attr("data-kanban-prompt") : null) || fallbackPrompt || "";

        if (prompt !== "") {
            promptThenSend($root, $card, columnId, prompt, onSuccess);
        } else {
            send($root, $card, columnId, {}, onSuccess);
        }
    }

    function findDropZones($root) {
        return $(SEL.dropZone).filter(function () {
            var selector = $(this).attr("data-kanban-board-for");
            return !selector || $root.is(selector);
        });
    }

    function initDropZone($zone) {
        if ($zone.data(READY)) {
            return;
        }
        $zone.data(READY, true);

        $zone.sortable({
            items: SEL.card,
            receive: function (event, ui) {
                var $card = ui.item.detach();
                var $root = ui.sender.closest(SEL.root);

                // Sortable keeps the last hovered container as the target after the pointer leaves it.
                if (!$zone.hasClass("is-over")) {
                    revert($root, $card);
                    return;
                }
                var columnId = $zone.attr("data-kanban-drop");
                var prompt = $zone.attr("data-kanban-prompt") || "";
                var onSuccess = function () {
                    $card.remove();
                    refresh($root);
                };

                if (prompt !== "") {
                    promptThenSend($root, $card, columnId, prompt, onSuccess);
                } else {
                    send($root, $card, columnId, {}, onSuccess);
                }
            },
            over: function () {
                $zone.addClass("is-over");
            },
            out: function () {
                $zone.removeClass("is-over");
            },
        });
    }

    function initBoard($root) {
        // Read-only boards still get refresh() (see init()); only sortable wiring is skipped here.
        if ($root.data(READY) || $root.attr("data-kanban-editable") !== "1") {
            return;
        }
        $root.data(READY, true);

        var $lists = $root.find(SEL.list);
        var $dropZones = findDropZones($root);
        $dropZones.each(function () {
            initDropZone($(this));
        });

        $lists.sortable({
            connectWith: $lists.add($dropZones),
            items: SEL.card + ":not(.is-locked)",
            cancel: "a, button, input, textarea, select",
            placeholder: "fi-kanban__placeholder",
            forcePlaceholderSize: true,
            tolerance: "pointer",
            revert: 100,
            start: function (event, ui) {
                ui.item.data(ORIGIN, { list: $(this), index: ui.item.index() });
                $dropZones.addClass("is-dragging");
                // The zones were display:none when sortable cached container sizes, so re-measure them.
                $(this).sortable("refreshPositions");
            },
            stop: function (event, ui) {
                $dropZones.removeClass("is-dragging is-over");

                var $card = ui.item;
                var origin = $card.data(ORIGIN);
                var $column = $card.closest(SEL.column);

                if (!origin || !$column.length || $card.parent()[0] === origin.list[0]) {
                    return;
                }

                var columnId = $column.attr("data-kanban-column");
                var prompt = $column.attr("data-kanban-prompt") || "";

                if (prompt !== "") {
                    promptThenSend($root, $card, columnId, prompt);
                } else {
                    send($root, $card, columnId, {});
                }
            },
        });
    }

    function init() {
        $(SEL.root).each(function () {
            var $root = $(this);
            initBoard($root);
            refresh($root);
        });
    }

    /**
     * Bridge between a modal opened for a `pending` move and the board that opened it. Both
     * entry points are idempotent no-ops once PENDING has already been cleared (e.g. the
     * `kanban` ext commits first, then the `modals` ext hides the modal, which later fires
     * `hidden.bs.modal` — by then PENDING is already null, so the revert below does nothing).
     */
    window.finestKanban = {
        commit: function (cardId) {
            if (PENDING && PENDING.cardId === String(cardId)) {
                PENDING = null;
                $(SEL.root).each(function () {
                    refresh($(this));
                });
            }
        },
        resend: function (cardId) {
            if (!PENDING || PENDING.cardId !== String(cardId)) {
                return;
            }
            var p = PENDING;
            PENDING = null;
            $(".modal-ajax").modal("hide");
            send(p.root, p.card, p.columnId, p.input, p.onSuccess);
        },
    };

    // The modal closed without the caller resolving the pending move (dismissed, navigated away
    // by its own follow-up, etc.) — put the card back where it was dropped from.
    $(document).on("hidden.bs.modal", ".modal-ajax", function () {
        if (!PENDING) {
            return;
        }
        var p = PENDING;
        PENDING = null;
        revert(p.root, p.card);
    });

    $(document).on("click", SEL.root + " " + SEL.moveTrigger, function (event) {
        event.preventDefault();

        var $trigger = $(this);
        var $root = $trigger.closest(SEL.root);
        var $card = $trigger.closest(SEL.card);

        if ($root.attr("data-kanban-editable") !== "1" || !$card.length || $card.hasClass("is-locked") || $root.hasClass("is-busy")) {
            return;
        }

        var $dropdown = $trigger.closest(".dropdown");
        if ($dropdown.length && $.fn.dropdown) {
            $dropdown.children('[data-toggle="dropdown"]').dropdown("hide");
        }

        moveCardTo($root, $card, $trigger.attr("data-kanban-move-to"), $trigger.attr("data-kanban-prompt"));
    });

    // Same guard as www/js/tables.js: $.nette.ext is only a function once nette.ajax's own
    // extensions module has run. A modal's own save signal answers payload.kanbanCommit (the
    // card id — the modal did the write itself) or payload.kanbanResend (the card id — the
    // modal only filled in what the move was missing, send the move again) instead of
    // redrawing anything, so this is the only place that observes them.
    if ($.nette && typeof $.nette.ext === "function") {
        $.nette.ext("kanban", {
            success: function (payload) {
                if (payload && payload.kanbanCommit != null) {
                    window.finestKanban.commit(payload.kanbanCommit);
                }
                if (payload && payload.kanbanResend != null) {
                    window.finestKanban.resend(payload.kanbanResend);
                }
            },
        });
    }

    // A snippet redraw replaces the board element; the new one is unflagged and gets wired again.
    // jQuery fires ajaxComplete after nette's snippet handler has already swapped the markup.
    $(document).ajaxComplete(init);
    $(init);
})(window, window.jQuery);
